import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import './LanguagePicker.css';

const languages = [
    { flag: '🇬🇧', native: 'English', english: 'English', search: 'english' },
    { flag: '🇪🇸', native: 'Español', english: 'Spanish', search: 'spanish español espanol' },
    { flag: '🇫🇷', native: 'Français', english: 'French', search: 'french français francais' },
    { flag: '🇮🇹', native: 'Italiano', english: 'Italian', search: 'italian italiano' },
    { flag: '🇩🇪', native: 'Deutsch', english: 'German', search: 'german deutsch' },
    { flag: '🇧🇷', native: 'Português', english: 'Portuguese', search: 'portuguese português portugues' },
    { flag: '🇷🇺', native: 'Русский', english: 'Russian', search: 'russian' },
    { flag: '🇨🇳', native: '中文', english: 'Chinese', search: 'chinese mandarin' },
    { flag: '🇯🇵', native: '日本語', english: 'Japanese', search: 'japanese' },
    { flag: '🇰🇷', native: '한국어', english: 'Korean', search: 'korean' },
    { flag: '🇸🇦', native: 'العربية', english: 'Arabic', search: 'arabic' },
    { flag: '🇳🇱', native: 'Nederlands', english: 'Dutch', search: 'dutch nederlands' },
    { flag: '🇵🇱', native: 'Polski', english: 'Polish', search: 'polish polski' },
    { flag: '🇸🇪', native: 'Svenska', english: 'Swedish', search: 'swedish svenska' },
    { flag: '🇹🇷', native: 'Türkçe', english: 'Turkish', search: 'turkish' },
    { flag: '🇮🇳', native: 'हिन्दी', english: 'Hindi', search: 'hindi' },
    { flag: '🇬🇷', native: 'Ελληνικά', english: 'Greek', search: 'greek' },
    { flag: '🇨🇿', native: 'Čeština', english: 'Czech', search: 'czech' },
    { flag: '🇷🇴', native: 'Română', english: 'Romanian', search: 'romanian' },
    { flag: '🇭🇺', native: 'Magyar', english: 'Hungarian', search: 'hungarian' },
    { flag: '🇩🇰', native: 'Dansk', english: 'Danish', search: 'danish dansk' },
    { flag: '🇫🇮', native: 'Suomi', english: 'Finnish', search: 'finnish suomi' },
    { flag: '🇳🇴', native: 'Norsk', english: 'Norwegian', search: 'norwegian norsk' },
    { flag: '🇮🇱', native: 'עברית', english: 'Hebrew', search: 'hebrew' },
    { flag: '🇹🇭', native: 'ภาษาไทย', english: 'Thai', search: 'thai' },
    { flag: '🇻🇳', native: 'Tiếng Việt', english: 'Vietnamese', search: 'vietnamese' },
    { flag: '🇮🇩', native: 'Bahasa Indonesia', english: 'Indonesian', search: 'indonesian bahasa' },
    { flag: '🇺🇦', native: 'Українська', english: 'Ukrainian', search: 'ukrainian' },
    { flag: '🇧🇩', native: 'বাংলা', english: 'Bengali', search: 'bengali' },
    { flag: '🇰🇪', native: 'Kiswahili', english: 'Swahili', search: 'swahili' },
];

const quickPicks = [
    { flag: '🇬🇧', native: 'English', english: 'English' },
    { flag: '🇪🇸', native: 'Español', english: 'Spanish' },
    { flag: '🇫🇷', native: 'Français', english: 'French' },
    { flag: '🇮🇹', native: 'Italiano', english: 'Italian' },
    { flag: '🇩🇪', native: 'Deutsch', english: 'German' },
    { flag: '🇧🇷', native: 'Português', english: 'Portuguese' },
];

const interfaceLanguageCodes = {
    English: 'en',
    Italian: 'it',
    Spanish: 'es',
    French: 'fr',
    Portuguese: 'pt',
};

function LanguagePicker() {
    const navigate = useNavigate();
    const { setInterfaceLanguage } = useLanguage();
    const [selected, setSelected] = useState(null);
    const [search, setSearch] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const filtered = languages.filter(l =>
        l.search.toLowerCase().includes(search.toLowerCase()) ||
        l.native.toLowerCase().includes(search.toLowerCase()) ||
        l.english.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (lang) => {
        setSelected(lang);
        setDropdownOpen(false);
        setSearch('');
        localStorage.setItem('vozoly_ui_language', lang.english);
    };

    const handleConfirm = () => {
        if (selected) {
            localStorage.setItem('vozoly_ui_language', selected.english);
            const code = interfaceLanguageCodes[selected.english];
            if (code) {
                setInterfaceLanguage(code);
            }
            localStorage.setItem('vozoly_onboarding_step', 'target-language');
            navigate('/onboarding/target-language');
        }
    };

    return (
        <div className="lp-screen">

            <div className="lp-top">
                <div className="lp-logo">🌍</div>
                <div className="lp-heading">Which language do you speak?</div>
                <div className="lp-heading-sub">Qué idioma hablas · Quelle langue parles-tu · Che lingua parli</div>
            </div>

            <div className="lp-middle">

                <div className="lp-search-wrap">
                    <div className="lp-search-box">
                        <span className="lp-search-icon">🔍</span>
                        <input
                            className="lp-search-input"
                            type="text"
                            placeholder="Search all languages..."
                            value={search}
                            onChange={e => { setSearch(e.target.value); setDropdownOpen(true); }}
                            onFocus={() => setDropdownOpen(true)}
                        />
                        {search && (
                            <button className="lp-clear-btn" onClick={() => { setSearch(''); setDropdownOpen(false); }}>✕</button>
                        )}
                    </div>
                </div>

                <div className="lp-dropdown-wrap">
                    <button
                        className={`lp-dropdown-btn ${selected ? 'has-value' : ''}`}
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                        <span className="lp-btn-flag">{selected ? selected.flag : '🌐'}</span>
                        <span className="lp-btn-text">
                            {selected ? `${selected.native} — ${selected.english}` : 'Choose from all languages'}
                        </span>
                        <span className={`lp-arrow ${dropdownOpen ? 'open' : ''}`}>▾</span>
                    </button>

                    {dropdownOpen && (
                        <div className="lp-dropdown-list">
                            {filtered.length === 0 ? (
                                <div className="lp-dropdown-empty">No language found for "{search}"</div>
                            ) : (
                                filtered.map(lang => (
                                    <div
                                        key={lang.english}
                                        className={`lp-dropdown-item ${selected && selected.english === lang.english ? 'selected' : ''}`}
                                        onClick={() => handleSelect(lang)}
                                    >
                                        <span className="lp-di-flag">{lang.flag}</span>
                                        <span className="lp-di-native">{lang.native}</span>
                                        <span className="lp-di-english">{lang.english}</span>
                                        {selected && selected.english === lang.english && <span className="lp-di-check">✓</span>}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <div className="lp-quick-label">Most common — tap to select</div>

                <div className="lp-quick-grid">
                    {quickPicks.map(lang => (
                        <div
                            key={lang.english}
                            className={`lp-quick-card ${selected && selected.english === lang.english ? 'selected' : ''}`}
                            onClick={() => handleSelect(lang)}
                        >
                            <div className="lp-qc-flag">{lang.flag}</div>
                            <div className="lp-qc-native">{lang.native}</div>
                            {selected && selected.english === lang.english && (
                                <div className="lp-qc-check">✓ Selected</div>
                            )}
                        </div>
                    ))}
                </div>

            </div>

            <div className="lp-footer">
                <button
                    className="lp-confirm-btn"
                    disabled={!selected}
                    onClick={handleConfirm}
                >
                    {selected ? `Continue in ${selected.native} →` : 'Select your language'}
                </button>
            </div>

        </div>
    );
}

export default LanguagePicker;